import React, { useState } from 'react';

// ============================================================================
// Types
// ============================================================================

export interface ErrorLogDetail {
  id: number;
  errorClass: string;
  message: string;
  source: string | null;
  emailSent: boolean;
  createdAt: string;
  context: Record<string, unknown> | null;
  backtrace: string[];
  aiPromptText: string;
  githubIssueUrl: string;
}

export interface AdminErrorLogDetailPageProps {
  errorLog: ErrorLogDetail;

  // URLs
  errorLogsPath: string;
  deleteErrorLogPath: string;
  csrfToken: string;

  // Labels
  labels: {
    title: string;
    backLabel: string;
    copyForAIButton: string;
    copiedButton: string;
    createIssueButton: string;
    deleteButton: string;
    deleteConfirm: string;
    // Details
    errorDetailsTitle: string;
    errorClassLabel: string;
    messageLabel: string;
    sourceLabel: string;
    notAvailable: string;
    emailSentLabel: string;
    yesLabel: string;
    noLabel: string;
    loggedAtLabel: string;
    agoLabel: string;
    // Context
    contextTitle: string;
    // Backtrace
    backtraceTitle: string;
  };
}

// ============================================================================
// Helper Functions
// ============================================================================

function formatTimeAgo(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMins < 60) return `${diffMins} minute${diffMins !== 1 ? 's' : ''}`;
  if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''}`;
  if (diffDays < 30) return `${diffDays} day${diffDays !== 1 ? 's' : ''}`;
  const diffMonths = Math.floor(diffDays / 30);
  return `${diffMonths} month${diffMonths !== 1 ? 's' : ''}`;
}

function formatDateTime(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    timeZoneName: 'short',
  });
}

// ============================================================================
// Main Component
// ============================================================================

export function AdminErrorLogDetailPage({
  errorLog,
  errorLogsPath,
  deleteErrorLogPath,
  csrfToken,
  labels,
}: AdminErrorLogDetailPageProps) {
  const [copied, setCopied] = useState(false);

  const handleCopyAIPrompt = async () => {
    try {
      await navigator.clipboard.writeText(errorLog.aiPromptText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleDelete = () => {
    if (window.confirm(labels.deleteConfirm)) {
      const form = document.createElement('form');
      form.method = 'POST';
      form.action = deleteErrorLogPath;

      const methodInput = document.createElement('input');
      methodInput.type = 'hidden';
      methodInput.name = '_method';
      methodInput.value = 'DELETE';
      form.appendChild(methodInput);

      const csrfInput = document.createElement('input');
      csrfInput.type = 'hidden';
      csrfInput.name = 'authenticity_token';
      csrfInput.value = csrfToken;
      form.appendChild(csrfInput);

      document.body.appendChild(form);
      form.submit();
    }
  };

  const hasContext = errorLog.context && Object.keys(errorLog.context).length > 0;

  return (
    <div>
      <div className="mb-4">
        <a href={errorLogsPath} className="text-decoration-none">
          <i className="bi bi-arrow-left me-1" /> {labels.backLabel}
        </a>
      </div>

      <div className="mb-4 d-flex justify-content-between align-items-center">
        <div>
          <h1 className="h2">
            <i className="bi bi-exclamation-triangle me-2 text-danger" />
            {labels.title} #{errorLog.id}
          </h1>
          <p className="text-muted">{formatDateTime(errorLog.createdAt)}</p>
        </div>
        <div className="d-flex gap-2">
          <button
            type="button"
            className={`btn ${copied ? 'btn-success' : 'btn-outline-primary'}`}
            onClick={handleCopyAIPrompt}
          >
            <i className={`bi ${copied ? 'bi-check' : 'bi-clipboard'} me-1`} />
            {copied ? labels.copiedButton : labels.copyForAIButton}
          </button>
          <a
            href={errorLog.githubIssueUrl}
            className="btn btn-outline-secondary"
            target="_blank"
            rel="noopener noreferrer"
          >
            <i className="bi bi-github me-1" />
            {labels.createIssueButton}
          </a>
          <button type="button" className="btn btn-outline-danger" onClick={handleDelete}>
            {labels.deleteButton}
          </button>
        </div>
      </div>

      {/* Error Details */}
      <div className="card shadow-sm mb-4">
        <div className="card-header bg-white">
          <h5 className="mb-0">
            <i className="bi bi-info-circle me-2" />
            {labels.errorDetailsTitle}
          </h5>
        </div>
        <div className="card-body">
          <div className="row mb-3">
            <div className="col-md-3">
              <strong>{labels.errorClassLabel}:</strong>
            </div>
            <div className="col-md-9">
              <code className="text-danger fs-5">{errorLog.errorClass}</code>
            </div>
          </div>

          <div className="row mb-3">
            <div className="col-md-3">
              <strong>{labels.messageLabel}:</strong>
            </div>
            <div className="col-md-9">
              <div className="bg-light p-3 rounded">{errorLog.message}</div>
            </div>
          </div>

          <div className="row mb-3">
            <div className="col-md-3">
              <strong>{labels.sourceLabel}:</strong>
            </div>
            <div className="col-md-9">
              {errorLog.source ? (
                <code>{errorLog.source}</code>
              ) : (
                <span className="text-muted fst-italic">{labels.notAvailable}</span>
              )}
            </div>
          </div>

          <div className="row mb-3">
            <div className="col-md-3">
              <strong>{labels.emailSentLabel}:</strong>
            </div>
            <div className="col-md-9">
              {errorLog.emailSent ? (
                <span className="badge bg-success">{labels.yesLabel}</span>
              ) : (
                <span className="badge bg-secondary">{labels.noLabel}</span>
              )}
            </div>
          </div>

          <div className="row">
            <div className="col-md-3">
              <strong>{labels.loggedAtLabel}:</strong>
            </div>
            <div className="col-md-9">
              {formatDateTime(errorLog.createdAt)} ({formatTimeAgo(errorLog.createdAt)}{' '}
              {labels.agoLabel})
            </div>
          </div>
        </div>
      </div>

      {/* Context */}
      {hasContext && (
        <div className="card shadow-sm mb-4">
          <div className="card-header bg-white">
            <h5 className="mb-0">
              <i className="bi bi-braces me-2" />
              {labels.contextTitle}
            </h5>
          </div>
          <div className="card-body">
            <pre
              className="bg-light p-3 rounded mb-0"
              style={{ whiteSpace: 'pre-wrap', wordWrap: 'break-word' }}
            >
              {JSON.stringify(errorLog.context, null, 2)}
            </pre>
          </div>
        </div>
      )}

      {/* Backtrace */}
      {errorLog.backtrace.length > 0 && (
        <div className="card shadow-sm">
          <div className="card-header bg-white">
            <h5 className="mb-0">
              <i className="bi bi-stack me-2" />
              {labels.backtraceTitle}
            </h5>
          </div>
          <div className="card-body">
            <pre
              className="bg-dark text-light p-3 rounded mb-0"
              style={{ fontSize: '0.85rem', maxHeight: 500, overflowY: 'auto' }}
            >
              {errorLog.backtrace.map((line, index) => (
                <React.Fragment key={index}>
                  <span className="text-muted">{String(index + 1).padStart(3, ' ')}</span> {line}
                  {'\n'}
                </React.Fragment>
              ))}
            </pre>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminErrorLogDetailPage;
